import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useVisitorTracking } from "@/hooks/useVisitorTracking";
import { LanguageProvider } from "@/i18n/LanguageContext";

// Eagerly load the landing page for FCP
import Index from "./pages/Index";

// Lazy load all other routes
const Jobs = lazy(() => import("./pages/Jobs"));
const CVBuilder = lazy(() => import("./pages/CVBuilder"));
const Diagnostics = lazy(() => import("./pages/Diagnostics"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const Support = lazy(() => import("./pages/Support"));
const SavedJobs = lazy(() => import("./pages/SavedJobs"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Go = lazy(() => import("./pages/Go"));
const Root = lazy(() => import("./pages/Root"));

const queryClient = new QueryClient();

function AppContent() {
  useVisitorTracking();
  
  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/saved-jobs" element={<SavedJobs />} />
        <Route path="/cv-builder" element={<CVBuilder />} />
        <Route path="/diagnostics" element={<Diagnostics />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/root" element={<Root />} />
        <Route path="/support" element={<Support />} />
        <Route path="/go" element={<Go />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <LanguageProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
